/**
 * @fileoverview Chat Context - Background Service for managing chat sessions
 *
 * This context acts as a global registry for active chat sessions, allowing:
 * - Persistence of chat state across component unmounts
 * - Continuation of streaming responses when navigating away
 * - Multiple concurrent chat sessions
 * - Centralized AbortController management
 */

import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import type { UIBlock, RenderPayload } from '@/types/ui-blocks';
import type { CardBlock } from '@/hooks/useCards';
import type { ChatAttachment, ChatCitation } from '@/services/DataService';
import type { StreamingParser } from '@/lib/streaming-parser';
import type { ToolStreamBlockMeta } from '@/lib/tool-stream-utils';

/**
 * Build the ChatContext session key for a conversation. Before a workflow
 * execution exists the key uses a `-pending` suffix; it becomes stable once
 * the execution id is known.
 *
 * @param workflowInstanceId - The workflow instance (agent) id.
 * @param workflowExecutionId - The execution id, or undefined before the first send.
 *
 * @returns The session key.
 */
export function getConversationSessionId(
  workflowInstanceId: string,
  workflowExecutionId?: string,
): string {
  return `${workflowInstanceId}-${workflowExecutionId || 'pending'}`;
}

type ToolCall = {
  id: string;
  name: string;
  description?: string;
  requires_approval?: boolean;
  hide_from_tool_call_chat_ui?: boolean
};

export type ChatEntry =
  | { id: string; kind: "assistant"; content: string; chat_history_id?: string; behaviour_action_state?: Record<string, unknown>; citations?: ChatCitation[] }
  | { id: string; kind: "assistant-structured"; payload: RenderPayload; chat_history_id?: string; behaviour_action_state?: Record<string, unknown>; citations?: ChatCitation[] }
  | { id: string; kind: "assistant-progressive"; blocks: UIBlock[]; isComplete?: boolean; chat_history_id?: string; behaviour_action_state?: Record<string, unknown>; citations?: ChatCitation[] }
  | { id: string; kind: "user"; content: string; blocks?: UIBlock[]; cardBlock?: CardBlock; chat_history_id?: string; created_at?: string; attachments?: ChatAttachment[] }
  | { id: string; kind: "assistant-typing" }
  | { id: string; kind: "reasoning"; steps: string[]; isStreaming: boolean; startTime?: number; duration?: number }
  | { id: string; kind: "tool-approval"; toolCalls: ToolCall[]; requireApproval?: boolean; resolved?: boolean; resolution?: 'approved' | 'rejected' };

export type Suggestion = {
  id: string | number;
  text: string;
  icon?: React.ReactNode;
  prefillOnly?: boolean;
};

interface ChatSession {
  sessionId: string;
  /** Chat mode selected for this conversation. Remembered per conversation across reloads (persisted to localStorage by the mode picker), with no global default; starts unset until the catalog default-fallback effect assigns it. */
  selectedModeId?: string;
  messages: ChatEntry[];
  isLoading: boolean;
  streamingContent: string;
  awaitingToolApproval: boolean;
  pendingToolCalls: ToolCall[];
  shouldStartThinkingSteps: boolean;
  typingMessageId: string | null;
  progressiveMessageId: string | null;
  reasoningMessageId: string | null;
  lastClosedReasoningId: string | null;
  dynamicSuggestions: Suggestion[];
  controller: AbortController | null;
  thinkingStepsTimer: NodeJS.Timeout | null;
  thinkingStepsInterval: NodeJS.Timeout | null;
  thinkingSteps: string[];

  // LangDiff streaming state
  streamingParser: StreamingParser | null;
  streamingTextContent: Map<number, string>; // blockIndex -> accumulated content
  streamingBlockIndices: Set<number>; // blocks currently streaming
  pendingBlockType: string | null; // type of block being loaded (for loading indicators)
  behavioursPending: boolean; // true when behaviours are streaming on a block (slot loading indicator)

  // Deferred tool execution (run execute_tools + CONTINUE after stream completes)
  deferredToolExecutionNeeded: boolean;

  // Interactive tool call tracking (select, confirmation, etc.)
  // Maps tool_call_id -> { blockType, blockIndex } for sending outputs with execute_tools
  pendingInteractiveToolCalls: Map<string, { blockType: string; blockIndex: number }>;
  // Collected outputs from user interactions, sent with the next execute_tools call
  collectedToolCallOutputs: Map<string, { id: string; output: unknown }>;

  // Per-block metadata (group_id, group_config) used for container assembly on stream completion
  toolStreamBlockMeta: Map<number, ToolStreamBlockMeta>;
}

interface ChatContextValue {
  sessions: Record<string, ChatSession>;
  registerSession: (sessionId: string, initialMessages?: ChatEntry[]) => void;
  getSession: (sessionId: string) => ChatSession | undefined;
  updateSession: (sessionId: string, updates: Partial<ChatSession>) => void;
  setMessages: (sessionId: string, messages: ChatEntry[] | ((prev: ChatEntry[]) => ChatEntry[])) => void;
  setLoading: (sessionId: string, isLoading: boolean) => void;
  setStreamingContent: (sessionId: string, content: string | ((prev: string) => string)) => void;
  setAwaitingToolApproval: (sessionId: string, awaiting: boolean) => void;
  setPendingToolCalls: (sessionId: string, calls: ToolCall[]) => void;
  setShouldStartThinkingSteps: (sessionId: string, should: boolean) => void;
  setTypingMessageId: (sessionId: string, id: string | null) => void;
  setProgressiveMessageId: (sessionId: string, id: string | null) => void;
  setReasoningMessageId: (sessionId: string, id: string | null) => void;
  setLastClosedReasoningId: (sessionId: string, id: string | null) => void;
  setDynamicSuggestions: (sessionId: string, suggestions: Suggestion[]) => void;
  getController: (sessionId: string) => AbortController | null;
  setController: (sessionId: string, controller: AbortController | null) => void;
  clearSession: (sessionId: string) => void;
  abortSession: (sessionId: string) => void;

  // LangDiff streaming methods
  setStreamingParser: (sessionId: string, parser: StreamingParser | null) => void;
  appendStreamingTextContent: (sessionId: string, blockIndex: number, chunk: string) => void;
  setStreamingBlockIndices: (sessionId: string, indices: Set<number>) => void;
  markBlockStreaming: (sessionId: string, blockIndex: number, isStreaming: boolean) => void;
  clearStreamingState: (sessionId: string) => void;
  setPendingBlockType: (sessionId: string, blockType: string | null) => void;
  setBehavioursPending: (sessionId: string, pending: boolean) => void;
  setDeferredToolExecutionNeeded: (sessionId: string, needed: boolean) => void;

  // Interactive tool call methods
  setPendingInteractiveToolCall: (sessionId: string, toolCallId: string, data: { blockType: string; blockIndex: number }) => void;
  resolvePendingInteractiveToolCall: (sessionId: string, toolCallId: string) => void;
  addToolCallOutput: (sessionId: string, toolCallId: string, output: unknown) => void;
  getAndClearToolCallOutputs: (sessionId: string) => Array<{ id: string; output: unknown }>;
  /**
   * Abandon the current interactive round: drop every unresolved interactive
   * tool call, any collected-but-unsent outputs, and the deferred-execution
   * flag. Used when the user answers an outstanding interactive component
   * (radio, editable_list, …) with a free-text message instead of clicking it.
   * Without this, the un-clicked call lingers in pendingInteractiveToolCalls
   * and permanently wedges the execute_tools gate (which requires
   * pendingInteractiveToolCalls.size === 0) for the rest of the session.
   */
  clearPendingInteractiveToolCalls: (sessionId: string) => void;

  // Group tracking methods
  setToolStreamBlockMeta: (sessionId: string, blockIndex: number, meta: ToolStreamBlockMeta) => void;
  getToolStreamBlockMeta: (sessionId: string) => Map<number, ToolStreamBlockMeta>;
  clearToolStreamState: (sessionId: string) => void;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

const createEmptySession = (sessionId: string): ChatSession => ({
  sessionId,
  selectedModeId: undefined,
  messages: [],
  isLoading: false,
  streamingContent: "",
  awaitingToolApproval: false,
  pendingToolCalls: [],
  shouldStartThinkingSteps: false,
  typingMessageId: null,
  progressiveMessageId: null,
  reasoningMessageId: null,
  lastClosedReasoningId: null,
  dynamicSuggestions: [],
  controller: null,
  thinkingStepsTimer: null,
  thinkingStepsInterval: null,
  thinkingSteps: [],

  // LangDiff streaming state
  streamingParser: null,
  streamingTextContent: new Map(),
  streamingBlockIndices: new Set(),
  pendingBlockType: null,
  behavioursPending: false,

  // Deferred tool execution
  deferredToolExecutionNeeded: false,

  // Interactive tool call tracking
  pendingInteractiveToolCalls: new Map(),
  collectedToolCallOutputs: new Map(),

  // Group tracking
  toolStreamBlockMeta: new Map(),
});

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<Record<string, ChatSession>>({});
  const sessionsRef = useRef<Record<string, ChatSession>>({});

  // Keep ref in sync with state
  sessionsRef.current = sessions;

  const registerSession = useCallback((sessionId: string, initialMessages: ChatEntry[] = []) => {
    setSessions((prev) => {
      if (prev[sessionId]) {
        // Session already exists, don't overwrite
        return prev;
      }
      return {
        ...prev,
        [sessionId]: {
          ...createEmptySession(sessionId),
          messages: initialMessages,
        },
      };
    });
  }, []);

  const getSession = useCallback((sessionId: string): ChatSession | undefined => {
    return sessionsRef.current[sessionId];
  }, []);

  const updateSession = useCallback((sessionId: string, updates: Partial<ChatSession>) => {
    setSessions((prev) => {
      let session = prev[sessionId];

      // Auto-register session if it doesn't exist
      if (!session) {
        session = createEmptySession(sessionId);
      }

      return {
        ...prev,
        [sessionId]: { ...session, ...updates },
      };
    });
  }, []);

  /**
   * Update session with immediate ref synchronization for synchronous access
   * in streaming callbacks. Without the immediate ref update, getSession() /
   * getController() return stale values until the next React render, which
   * breaks guards that compare controller signals or read progressive IDs.
   */
  const updateSessionImmediate = useCallback((sessionId: string, updates: Partial<ChatSession>) => {
    if (sessionsRef.current[sessionId]) {
      sessionsRef.current = {
        ...sessionsRef.current,
        [sessionId]: { ...sessionsRef.current[sessionId], ...updates },
      };
    }
    updateSession(sessionId, updates);
  }, [updateSession]);

  const setMessages = useCallback(
    (sessionId: string, messages: ChatEntry[] | ((prev: ChatEntry[]) => ChatEntry[])) => {
      setSessions((prev) => {
        let session = prev[sessionId];

        // Auto-register session if it doesn't exist
        if (!session) {
          session = createEmptySession(sessionId);
        }

        const newMessages = typeof messages === 'function' ? messages(session.messages) : messages;
        return {
          ...prev,
          [sessionId]: { ...session, messages: newMessages },
        };
      });
    },
    []
  );

  const setLoading = useCallback((sessionId: string, isLoading: boolean) => {
    updateSession(sessionId, { isLoading });
  }, [updateSession]);

  const setStreamingContent = useCallback(
    (sessionId: string, content: string | ((prev: string) => string)) => {
      setSessions((prev) => {
        const session = prev[sessionId];
        if (!session) return prev;
        const newContent = typeof content === 'function' ? content(session.streamingContent) : content;
        return {
          ...prev,
          [sessionId]: { ...session, streamingContent: newContent },
        };
      });
    },
    []
  );

  const setAwaitingToolApproval = useCallback((sessionId: string, awaiting: boolean) => {
    updateSession(sessionId, { awaitingToolApproval: awaiting });
  }, [updateSession]);

  const setPendingToolCalls = useCallback((sessionId: string, calls: ToolCall[]) => {
    updateSession(sessionId, { pendingToolCalls: calls });
  }, [updateSession]);

  const setShouldStartThinkingSteps = useCallback((sessionId: string, should: boolean) => {
    updateSession(sessionId, { shouldStartThinkingSteps: should });
  }, [updateSession]);

  const setTypingMessageId = useCallback((sessionId: string, id: string | null) => {
    updateSession(sessionId, { typingMessageId: id });
  }, [updateSession]);

  const setProgressiveMessageId = useCallback((sessionId: string, id: string | null) => {
    updateSessionImmediate(sessionId, { progressiveMessageId: id });
  }, [updateSessionImmediate]);

  const setReasoningMessageId = useCallback((sessionId: string, id: string | null) => {
    updateSession(sessionId, { reasoningMessageId: id });
  }, [updateSession]);

  const setLastClosedReasoningId = useCallback((sessionId: string, id: string | null) => {
    updateSession(sessionId, { lastClosedReasoningId: id });
  }, [updateSession]);

  const setDynamicSuggestions = useCallback((sessionId: string, suggestions: Suggestion[]) => {
    updateSession(sessionId, { dynamicSuggestions: suggestions });
  }, [updateSession]);

  const getController = useCallback((sessionId: string): AbortController | null => {
    return sessionsRef.current[sessionId]?.controller || null;
  }, []);

  const setController = useCallback((sessionId: string, controller: AbortController | null) => {
    updateSessionImmediate(sessionId, { controller });
  }, [updateSessionImmediate]);

  const clearSession = useCallback((sessionId: string) => {
    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;

      // Clean up timers
      if (session.thinkingStepsTimer) clearTimeout(session.thinkingStepsTimer);
      if (session.thinkingStepsInterval) clearInterval(session.thinkingStepsInterval);

      // Create fresh session
      return {
        ...prev,
        [sessionId]: createEmptySession(sessionId),
      };
    });
  }, []);

  const abortSession = useCallback((sessionId: string) => {
    const session = sessionsRef.current[sessionId];
    if (session?.controller) {
      session.controller.abort();
      updateSession(sessionId, { controller: null, isLoading: false });
    }
  }, [updateSession]);

  // LangDiff streaming methods
  const setStreamingParser = useCallback((sessionId: string, parser: StreamingParser | null) => {
    updateSessionImmediate(sessionId, { streamingParser: parser });
  }, [updateSessionImmediate]);

  const appendStreamingTextContent = useCallback((sessionId: string, blockIndex: number, chunk: string) => {
    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;

      const newMap = new Map(session.streamingTextContent);
      const currentContent = newMap.get(blockIndex) || '';
      newMap.set(blockIndex, currentContent + chunk);

      return {
        ...prev,
        [sessionId]: { ...session, streamingTextContent: newMap },
      };
    });
  }, []);

  const setStreamingBlockIndices = useCallback((sessionId: string, indices: Set<number>) => {
    updateSession(sessionId, { streamingBlockIndices: indices });
  }, [updateSession]);

  const markBlockStreaming = useCallback((sessionId: string, blockIndex: number, isStreaming: boolean) => {
    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;

      const newIndices = new Set(session.streamingBlockIndices);
      if (isStreaming) {
        newIndices.add(blockIndex);
      } else {
        newIndices.delete(blockIndex);
      }

      return {
        ...prev,
        [sessionId]: { ...session, streamingBlockIndices: newIndices },
      };
    });
  }, []);

  const clearStreamingState = useCallback((sessionId: string) => {
    // Reset parser outside setState to avoid side effects in callback
    const parser = sessionsRef.current[sessionId]?.streamingParser;
    parser?.reset();

    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;

      return {
        ...prev,
        [sessionId]: {
          ...session,
          streamingParser: null,
          streamingContent: "",
          streamingTextContent: new Map(),
          streamingBlockIndices: new Set(),
          pendingBlockType: null,
          behavioursPending: false,
        },
      };
    });
  }, []);

  const setPendingBlockType = useCallback((sessionId: string, blockType: string | null) => {
    updateSession(sessionId, { pendingBlockType: blockType });
  }, [updateSession]);

  const setBehavioursPending = useCallback((sessionId: string, pending: boolean) => {
    updateSession(sessionId, { behavioursPending: pending });
  }, [updateSession]);

  const setDeferredToolExecutionNeeded = useCallback((sessionId: string, needed: boolean) => {
    updateSessionImmediate(sessionId, { deferredToolExecutionNeeded: needed });
  }, [updateSessionImmediate]);

  // Interactive tool call methods
  const setPendingInteractiveToolCall = useCallback((sessionId: string, toolCallId: string, data: { blockType: string; blockIndex: number }) => {
    // Use immediate update: registration happens while a stream is being
    // processed, and the TOOL_CALLS / onDone handlers in the same stream read
    // this map synchronously via getSession() (sessionsRef) to decide whether
    // to hold execute_tools for user input. A batched update is only visible
    // after the next render — if the stream's tail events are processed before
    // React flushes, the gate reads size 0 and execute_tools fires without the
    // user's input (the interactive element's output is then fabricated
    // server-side and the real selection is lost).
    const session = sessionsRef.current[sessionId];
    if (!session) return;
    const newMap = new Map(session.pendingInteractiveToolCalls);
    newMap.set(toolCallId, data);
    updateSessionImmediate(sessionId, { pendingInteractiveToolCalls: newMap });
  }, [updateSessionImmediate]);

  const resolvePendingInteractiveToolCall = useCallback((sessionId: string, toolCallId: string) => {
    // Use immediate update so the change is visible synchronously in handleUIAction
    // (setSessions batches updates and getSession reads from sessionsRef)
    const session = sessionsRef.current[sessionId];
    if (!session) return;
    const newMap = new Map(session.pendingInteractiveToolCalls);
    newMap.delete(toolCallId);
    updateSessionImmediate(sessionId, { pendingInteractiveToolCalls: newMap });
  }, [updateSessionImmediate]);

  const addToolCallOutput = useCallback((sessionId: string, toolCallId: string, output: unknown) => {
    // Use immediate update so the change is visible synchronously in handleUIAction
    const session = sessionsRef.current[sessionId];
    if (!session) return;
    const newMap = new Map(session.collectedToolCallOutputs);
    newMap.set(toolCallId, { id: toolCallId, output });
    updateSessionImmediate(sessionId, { collectedToolCallOutputs: newMap });
  }, [updateSessionImmediate]);

  const getAndClearToolCallOutputs = useCallback((sessionId: string): Array<{ id: string; output: unknown }> => {
    const session = sessionsRef.current[sessionId];
    if (!session || session.collectedToolCallOutputs.size === 0) return [];
    const outputs = Array.from(session.collectedToolCallOutputs.values());
    // Clear via immediate update
    updateSessionImmediate(sessionId, { collectedToolCallOutputs: new Map() });
    return outputs;
  }, [updateSessionImmediate]);

  const clearPendingInteractiveToolCalls = useCallback((sessionId: string) => {
    const session = sessionsRef.current[sessionId];
    if (!session) return;
    // Immediate update so the cleared gate is visible synchronously to any
    // handleStream/onDone that runs right after the user's message is sent.
    updateSessionImmediate(sessionId, {
      pendingInteractiveToolCalls: new Map(),
      collectedToolCallOutputs: new Map(),
      deferredToolExecutionNeeded: false,
    });
  }, [updateSessionImmediate]);

  // Group tracking methods
  const setToolStreamBlockMeta = useCallback((sessionId: string, blockIndex: number, meta: ToolStreamBlockMeta) => {
    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;
      const newMeta = new Map(session.toolStreamBlockMeta);
      newMeta.set(blockIndex, meta);
      return { ...prev, [sessionId]: { ...session, toolStreamBlockMeta: newMeta } };
    });
  }, []);

  const getToolStreamBlockMeta = useCallback((sessionId: string): Map<number, ToolStreamBlockMeta> => {
    return sessionsRef.current[sessionId]?.toolStreamBlockMeta ?? new Map();
  }, []);

  const clearToolStreamState = useCallback((sessionId: string) => {
    // Only clear block meta (used for group assembly, no longer needed after onDone).
    // pendingInteractiveToolCalls and collectedToolCallOutputs are preserved —
    // they're needed until the user interacts and execute_tools runs.
    // They get cleared via resolvePendingInteractiveToolCall and getAndClearToolCallOutputs.
    updateSession(sessionId, {
      toolStreamBlockMeta: new Map(),
    });
  }, [updateSession]);

  const value: ChatContextValue = {
    sessions,
    registerSession,
    getSession,
    updateSession,
    setMessages,
    setLoading,
    setStreamingContent,
    setAwaitingToolApproval,
    setPendingToolCalls,
    setShouldStartThinkingSteps,
    setTypingMessageId,
    setProgressiveMessageId,
    setReasoningMessageId,
    setLastClosedReasoningId,
    setDynamicSuggestions,
    getController,
    setController,
    clearSession,
    abortSession,

    // LangDiff streaming methods
    setStreamingParser,
    appendStreamingTextContent,
    setStreamingBlockIndices,
    markBlockStreaming,
    clearStreamingState,
    setPendingBlockType,
    setBehavioursPending,
    setDeferredToolExecutionNeeded,

    // Interactive tool call methods
    setPendingInteractiveToolCall,
    resolvePendingInteractiveToolCall,
    addToolCallOutput,
    getAndClearToolCallOutputs,
    clearPendingInteractiveToolCalls,

    // Group tracking methods
    setToolStreamBlockMeta,
    getToolStreamBlockMeta,
    clearToolStreamState,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within ChatProvider');
  }
  return context;
};
